import { AppBar, Toolbar, Box, Icon, Tooltip, Button } from "@mui/material";

import StrategiesDropdown from "./StrategiesDropdown";
import IgnitionRegimeDropdown from "./IgnitionRegimeDropdown";
import CustomDatePicker from "./DatePickerComponent";
import TreatmentsDropdown from "./TreatmentsDropdown";
import { useStrategy } from "../api/strategy";
import { useContext, useEffect, useRef, useState } from "react";
import { StrategyContext } from "../contexts/StrategyContext";
import { useAutoRecalculate } from "../hooks/useAutoRecalculate";
import { useAuth } from "../contexts/AuthContext";
import { isViewOnly } from "../utils/permissions";
import { SB } from "./scenarioBar/tokens";

export default function StrategiesBar() {
  const { selectedStrategy, strategyRefreshToken } = useContext(StrategyContext);
  const { isOutOfDate, isLoading, recalculate } = useStrategy();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const autoRecalculate = useAutoRecalculate();

  // Play a single bounce each time an edit makes the fuel loads out of date
  // (rather than animating continuously). The persistent red colour signals the
  // stale state until the user recalculates.
  const [bounce, setBounce] = useState(false);
  const prevToken = useRef(strategyRefreshToken);
  useEffect(() => {
    if (strategyRefreshToken !== prevToken.current) {
      prevToken.current = strategyRefreshToken;
      if (selectedStrategy) {
        setBounce(false);
        // Restart the animation even if a previous bounce is still mid-flight.
        requestAnimationFrame(() => setBounce(true));
      }
    }
  }, [strategyRefreshToken, selectedStrategy]);

  return (
    <AppBar
      position="sticky"
      sx={{
        width: "100%",
        height: 52,
        backgroundColor: "#212b37",
        color: "white",
        zIndex: 1000,
      }}
      elevation={0}
    >
      <Toolbar
        sx={{
          "&.MuiToolbar-root": {
            minHeight: 52,
            display: "flex",
            flexDirection: "row",
          },
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              height: 30,
              width: 30,
              mr: 3, // Add right margin for spacing
              display: "flex",
              flexDirection: "row",
            }}
          />
          {/* Strategy › Treatments › Ignitions: one labelled chain, because
              treatments belong to the strategy and ignitions run against it.
              Neutral segments in a shared well; only the open one is tinted. */}
          <Box
            component="nav"
            aria-label="Scenario"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              height: 44,
              p: "3px",
              borderRadius: "12px",
              background: SB.well,
              boxShadow:
                "inset 0 0 0 1px rgba(255,255,255,0.07), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            <StrategiesDropdown />
            <TreatmentsDropdown />
            <IgnitionRegimeDropdown />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          {!autoRecalculate && !viewOnly && (
          <Tooltip
            title={
              isOutOfDate
                ? "Fuel loads are out of date — click to recalculate"
                : "Recalculate fuel loads"
            }
            arrow
          >
            <span>
              <Button
                variant="contained"
                size="small"
                onClick={recalculate}
                onAnimationEnd={() => setBounce(false)}
                disabled={!selectedStrategy || isLoading}
                startIcon={<Icon sx={{ fontSize: 18 }}>calculate</Icon>}
                sx={{
                  backgroundColor: isOutOfDate ? "#d32f2f" : "#00d4ad",
                  color: isOutOfDate ? "#fff" : "#000",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": {
                    backgroundColor: isOutOfDate ? "#b71c1c" : "#00bfa0",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(255,255,255,0.12)",
                    color: "rgba(255,255,255,0.3)",
                  },
                  animation: bounce ? "recalc-bounce 0.5s ease" : "none",
                  "@keyframes recalc-bounce": {
                    "0%, 100%": { transform: "translateY(0)" },
                    "30%": { transform: "translateY(-6px)" },
                    "60%": { transform: "translateY(-3px)" },
                  },
                }}
              >
                Recalculate
              </Button>
            </span>
          </Tooltip>
          )}
          <CustomDatePicker />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
